// @ts-nocheck
/**
 * useFKLookup — Pre-built hooks for FK entity lookups
 * Each hook returns {options, isLoading} where options = [{id, label, subLabel, searchTags, original}]
 *
 * FKOption.label      → Primary display text (shown in trigger button)
 * FKOption.subLabel   → Secondary badge (code, MST, etc.)
 * FKOption.meta       → Extra info line in dropdown (specs, size, tax ID)
 * FKOption.searchTags → Concatenated string for full-text search (code + name + MST + specs)
 * FKOption.original   → Raw API object for inheriting fields
 */

import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { technicalApi } from '@/lib/api-client';
import { getUploadUrl } from '@/lib/utils';

export interface FKOption {
    id: string;
    label: string;
    subLabel?: string;
    /** Extra info line shown below label in the dropdown list (specs, MST, etc.) */
    meta?: string;
    /** Tiny thumbnail URL for visual identification (product image, avatar, etc.) */
    thumbnail?: string;
    /** Concatenated search string for full-text matching across all relevant fields */
    searchTags?: string;
    subItemType?: string;
    original?: any;
}

// ── Customers ──────────────────────────────────────────

export function useCustomerLookup(params?: { hasDeliverableOrders?: boolean; enabled?: boolean; search?: string; limit?: number }) {
    const { data, isLoading, error } = useQuery({
        queryKey: ['customers-lookup', params],
        queryFn: () => trpcClient.customers.list.query({ page: 1, limit: params?.limit || 100, search: params?.search, ...params }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        retry: 2,
        enabled: params?.enabled !== false,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((c: any) => {
        const name = c.name || c.companyName || c.customerName || '—';
        const shortName = c.shortName ? c.shortName : name;
        const code = c.code || c.customerId || '';
        const taxId = c.taxId || c.taxCode || c.mst || '';
        const phone = c.phone || c.tel || '';

        return {
            id: c.id,
            label: shortName,
            meta: [
                name !== shortName ? name : null,
                taxId ? `MST: ${taxId}` : null,
                phone ? `📞 ${phone}` : null,
            ].filter(Boolean).join(' · ') || undefined,
            searchTags: [code, name, shortName, taxId, phone].filter(Boolean).join(' ').toLowerCase(),
            original: c,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading, error };
}

// ── Products ───────────────────────────────────────────

export function useProductLookup(customerId?: string, enabled = true, search?: string, limit = 100) {
    const { data, isLoading, error } = useQuery({
        queryKey: ['products-lookup', customerId || 'all', search || ''],
        queryFn: () => {
            const params: any = { page: 1, limit, search };
            if (customerId) params.customerId = customerId;
            return trpcClient.products.list.query(params);
        },
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        retry: 2,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((p: any) => {
        const code = p.productCode || '';
        const name = p.productName || '—';
        const unit = p.unit || '';
        const customerName = p.customer?.shortName || p.customer?.name || '';

        // Build specs string: finishedSize or packingSpec
        const size = p.finishedSize
            ? `${(p.finishedSize as any)?.length || ''}×${(p.finishedSize as any)?.width || ''}mm`
            : '';
        const specs = [p.packingSpec, size, unit].filter(Boolean).join(' · ');
        const meta = [specs, customerName ? `KH: ${customerName}` : null].filter(Boolean).join(' | ') || undefined;

        return {
            id: p.id,
            label: name,
            meta,
            thumbnail: p.image ? getUploadUrl(p.image) : undefined,
            searchTags: [code, name, p.packingSpec, size, p.material?.name, customerName].filter(Boolean).join(' ').toLowerCase(),
            original: p,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading, error };
}

// ── Materials ──────────────────────────────────────────

export function useMaterialLookup(enabled = true, search?: string, limit = 100, typeId?: string) {
    const { data, isLoading } = useQuery({
        queryKey: ['materials-lookup', search || '', typeId || 'all', limit],
        queryFn: () => trpcClient.materials.list.query({ page: 1, limit, search, typeId: typeId || undefined }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((m: any) => {
        const code = m.materialId || '';
        const name = m.name || '—';
        const gsm = m.gsm ? `${m.gsm}gsm` : '';
        const size = (m.width || m.length) ? `${m.width || '?'}×${m.length || '?'}mm` : '';
        const specs = [gsm, size, m.specs].filter(Boolean).join(' · ');

        return {
            id: m.id,
            label: name,
            meta: specs || undefined,
            searchTags: [code, name, m.specs, gsm, m.category, m.vendor?.name].filter(Boolean).join(' ').toLowerCase(),
            original: m,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Vendors ────────────────────────────────────────────

export function useVendorLookup(enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['vendors-lookup', search || ''],
        queryFn: () => trpcClient.vendors.list.query({ page: 1, limit, search }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((v: any) => {
        const name = v.name || v.companyName || '—';
        const shortName = v.shortName || name;
        const code = v.code || v.vendorId || '';
        const taxId = v.taxId || v.taxCode || v.mst || '';
        const phone = v.phone || v.tel || '';

        return {
            id: v.id,
            label: shortName,
            meta: [
                name !== shortName ? name : null,
                taxId ? `MST: ${taxId}` : null,
                phone ? `📞 ${phone}` : null,
            ].filter(Boolean).join(' · ') || undefined,
            searchTags: [code, name, shortName, taxId, phone].filter(Boolean).join(' ').toLowerCase(),
            original: v,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Orders ─────────────────────────────────────────────

export function useOrderLookup(customerId?: string, status?: string | string[], enabled = true, search?: string, limit = 100, hasAvailableFG?: boolean) {
    const { data, isLoading } = useQuery({
        queryKey: ['orders-lookup', customerId || 'all', status || 'all', search || '', hasAvailableFG],
        queryFn: () => trpcClient.orders.list.query({ page: 1, limit, customerId: customerId || undefined, status, search, hasAvailableFG }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((o: any) => {
        const orderId = o.orderId || o.id;
        const custName = o.customer?.shortName || o.customer?.name || o.customerName || '';
        return {
            id: o.id,
            label: orderId,
            subLabel: custName ? `[${custName}]` : undefined,
            meta: o.orderDetails?.[0]?.poReference ? `PO: ${o.orderDetails?.[0]?.poReference}` : undefined,
            searchTags: [orderId, custName, o.orderDetails?.[0]?.poReference].filter(Boolean).join(' ').toLowerCase(),
            original: o,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Production Orders ─────────────────────────────────────────────

export function useProductionOrderLookup(status?: string | string[], enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['production-orders-lookup', status || 'all', search || ''],
        queryFn: () => trpcClient.productionOrders.list.query({ page: 1, limit, status, search }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((o: any) => {
        const orderId = o.orderId || o.id;
        const productName = o.productName || o.product?.productName || '';
        const poReference = o.poReference || '';
        return {
            id: o.id,
            label: orderId,
            subLabel: poReference ? `[${poReference}]` : undefined,
            meta: productName ? `SP: ${productName}` : undefined,
            searchTags: [orderId, poReference, productName].filter(Boolean).join(' ').toLowerCase(),
            original: o,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Production Tasks ─────────────────────────────────────────────

export function useProductionTaskLookup(machineId?: string, status?: string | string[], enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['production-tasks-lookup', machineId || 'all', status || 'all', search || ''],
        queryFn: () => trpcClient.productionTasks.list.query({ page: 1, limit, machineId: machineId || undefined, status, search } as any),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((t: any) => {
        const taskId = t.taskId || t.id;
        const machine = t.machine?.name || t.machineId || '';
        const po = t.productionOrder?.orderId || t.productionOrderId || '';
        const product = t.productionOrder?.productName || '';
        
        return {
            id: t.id,
            label: taskId,
            subLabel: po ? `[${po}]` : undefined,
            meta: [machine ? `Máy: ${machine}` : null, product ? `SP: ${product}` : null].filter(Boolean).join(' | ') || undefined,
            searchTags: [taskId, machine, po, product].filter(Boolean).join(' ').toLowerCase(),
            original: t,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Product Types ──────────────────────────────────────

export function useProductTypeLookup(enabled = true) {
    const { data, isLoading } = useQuery({
        queryKey: ['product-types-lookup'],
        queryFn: async () => {
            try {
                return await trpcClient.config.getProductTypes.query();
            } catch {
                return [];
            }
        },
        staleTime: 10 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const options: FKOption[] = items.map((t: any) => ({
        id: t.id,
        label: t.name || t.value || t.group || t.code || '—',
        searchTags: [t.name, t.code, t.group, t.category].filter(Boolean).join(' ').toLowerCase(),
        original: t,
    })).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Material Types ─────────────────────────────────────

export function useMaterialTypeLookup(enabled = true) {
    const { data, isLoading } = useQuery({
        queryKey: ['material-types-lookup'],
        queryFn: async () => {
            try {
                return await trpcClient.config.getMaterialTypes.query();
            } catch {
                return [];
            }
        },
        staleTime: 10 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const options: FKOption[] = items.map((t: any) => {
        const materialName = t.material || t.name;
        const displayName = materialName ? (t.code ? `${t.code} ${materialName}` : materialName) : (t.code || t.group || '—');
        
        return {
            id: t.id,
            label: displayName,
            subLabel: t.group ? `Nhóm: ${t.group}` : undefined,
            meta: t.gsm ? `GSM: ${t.gsm}` : undefined,
            searchTags: [t.code, materialName, t.group, t.gsm].filter(Boolean).join(' ').toLowerCase(),
            original: t,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Processes ──────────────────────────────────────────

export function useProcessLookup(enabled = true) {
    const { data, isLoading } = useQuery({
        queryKey: ['processes-lookup'],
        queryFn: async () => {
            try {
                return await trpcClient.config.getProcesses.query();
            } catch {
                return [];
            }
        },
        staleTime: 10 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const sorted = [...items].sort((a: any, b: any) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
    const options: FKOption[] = sorted.map((p: any) => ({
        id: p.id,
        label: p.name || '—',
        searchTags: [p.stepId, p.name].filter(Boolean).join(' ').toLowerCase(),
        original: p,
    }));

    return { options, isLoading };
}

// ── Units (from Category type='unit') ──────────────────

export function useUnitLookup(enabled = true) {
    const { data, isLoading } = useQuery({
        queryKey: ['units-lookup'],
        queryFn: async () => {
            try {
                return await trpcClient.config.getUnits.query();
            } catch {
                return [];
            }
        },
        staleTime: 10 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const options: FKOption[] = items.map((u: any) => ({
        id: u.name,
        label: u.name,
        original: u,
    })).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Users (NV Kinh doanh / Employees) ──────────────────

export function useUserLookup(role?: string, enabled = true, search?: string, limit = 1000) {
    const { data, isLoading } = useQuery({
        queryKey: ['users-lookup', role || 'all', search || ''],
        queryFn: () => trpcClient.users.list.query({ page: 1, limit, search, ...(role ? { role } : {}) } as any),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items
        .filter((u: any) => u.status === 'ACTIVE' || !u.status)
        .map((u: any) => {
            const name = u.fullName || '—';
            const code = u.employeeCode || u.userId || '';
            const dept = u.department || '';
            const position = u.position || '';

            return {
                id: u.id,
                label: name,
                meta: [dept, position].filter(Boolean).join(' · ') || undefined,
                searchTags: [code, name, dept, position].filter(Boolean).join(' ').toLowerCase(),
                original: u,
            };
        });

    return { options, isLoading };
}

// ── Roles ──────────────────────────────────────────────

export function useRoleLookup(enabled = true) {
    const { data, isLoading } = useQuery({
        queryKey: ['roles-lookup'],
        queryFn: async () => {
            try {
                const res = await trpcClient.roles.list.query();
                return res ?? [];
            } catch {
                return [];
            }
        },
        staleTime: 10 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    // User.role lưu mã vai trò (Role.roleId: ADMIN, USER...) nên dùng roleId làm giá trị lọc
    const options: FKOption[] = items.map((r: any) => ({
        id: r.roleId || r.code || r.id,
        label: r.name || r.code || r.id,
        subLabel: r.roleId || r.code,
        meta: r.description,
        searchTags: [r.code, r.name, r.description].filter(Boolean).join(' ').toLowerCase(),
        original: r,
    })).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Die Cuts ───────────────────────────────────────────

export function useMoldLookup(productId?: string, customerId?: string, enabled = true, search?: string, limit = 100, dimensions?: { length?: number | null; width?: number | null }) {
    const { data, isLoading } = useQuery({
        queryKey: ['diecuts-lookup', productId || '', customerId || '', search || '', dimensions || ''],
        queryFn: async () => {
            // Use suggest endpoint if all dimensions are provided
            if (dimensions && dimensions.length && dimensions.width) {
                const res = await (trpcClient as any).technical.molds.suggest.query({
                    productLength: dimensions.length,
                    productWidth: dimensions.width,
                    customerId: customerId || undefined
                });
                return res ?? [];
            }
            
            // Fallback to normal list query
            const params: any = { limit, search };
            if (productId) params.productId = productId;
            if (customerId) params.customerId = customerId;
            const res = await (trpcClient as any).technical.molds.list.query(params);
            return res?.items ?? [];
        },
        staleTime: 3 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const options: FKOption[] = items.map((d: any) => ({
        id: d.id,
        label: `${d.name || '—'} (v${d.version || 1})`,
        meta: [d.size, d.type, d.productName].filter(Boolean).join(' · ') || undefined,
        searchTags: [d.dieCutId, d.name, d.size, d.type, d.productName].filter(Boolean).join(' ').toLowerCase(),
        original: d,
    }));

    return { options, isLoading };
}

// ── Plates ─────────────────────────────────────────────

export function usePlateLookup(productId?: string, customerId?: string, enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['plates-lookup', productId || '', customerId || '', search || ''],
        queryFn: async () => {
            const params: any = { limit, search };
            if (productId) params.productId = productId;
            if (customerId) params.customerId = customerId;
            const res = await (trpcClient as any).technical.plates.list.query(params);
            return res?.items ?? [];
        },
        staleTime: 3 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const options: FKOption[] = items.map((p: any) => ({
        id: p.id,
        label: `${p.name || '—'} (v${p.version || 1})`,
        meta: [p.size, p.colors?.join(', '), p.productName].filter(Boolean).join(' · ') || undefined,
        searchTags: [p.plateId, p.name, p.size, p.productName].filter(Boolean).join(' ').toLowerCase(),
        original: p,
    }));

    return { options, isLoading };
}

// ── Layouts ────────────────────────────────────────────

export function useLayoutLookup(enabled = true) {
    const { data, isLoading } = useQuery({
        queryKey: ['layouts-lookup'],
        queryFn: async () => {
            const res = await (trpcClient as any).technical.layouts.list.query({ limit: 5000 });
            return res?.items ?? [];
        },
        staleTime: 3 * 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const options: FKOption[] = items.map((l: any) => ({
        id: l.id,
        label: l.name || '—',
        meta: [l.sheetSize, l.ups ? `${l.ups} ups` : undefined].filter(Boolean).join(' · ') || undefined,
        searchTags: [l.layoutId, l.name, l.sheetSize, l.productName].filter(Boolean).join(' ').toLowerCase(),
        original: l,
    }));

    return { options, isLoading };
}

export function usePrintLayoutLookup(enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['print-layouts-lookup', search || ''],
        queryFn: async () => {
            const res = await (trpcClient as any).prepress.listPrintLayouts.query({ page: 1, limit, search });
            return res?.items ?? [];
        },
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = Array.isArray(data) ? data : [];
    const options: FKOption[] = items.map((l: any) => {
        const code = l.layoutCode || '';
        const name = l.name || '—';
        const sheet = l.sheetSize ? `Khổ: ${l.sheetSize}` : '';
        const ups = l.ups ? `${l.ups} ups` : '';
        const specs = [sheet, ups, l.prepressType].filter(Boolean).join(' · ');

        return {
            id: l.id,
            label: name,
            subLabel: code ? `[${code}]` : undefined,
            meta: specs || undefined,
            searchTags: [code, name, l.sheetSize, l.prepressType].filter(Boolean).join(' ').toLowerCase(),
            original: l,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

export function useMachineLookup(enabled = true) {
    const { data, isLoading, error } = useQuery({
        queryKey: ['machines-lookup'],
        queryFn: () => trpcClient.machines.list.query({ page: 1, limit: 1000 }),
        staleTime: 10 * 60 * 1000,
        retry: 2,
        enabled,
    });
    
    return {
        options: (data?.items?.map((item: any) => ({
            id: item.id,
            label: item.name || item.machineId,
        })) || []).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi')),
        isLoading,
        error,
        data: data?.items,
    };
}

// ── Warehouses ─────────────────────────────────────────

export function useWarehouseLookup(type?: string, enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['warehouses-lookup', type || 'all', search || ''],
        queryFn: () => trpcClient.warehouses.list.query({ page: 1, limit, type: type || undefined, search }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((w: any) => {
        const code = w.code || '';
        const name = w.name || '—';
        const typeLabel = w.type ? `[${w.type}]` : '';
        return {
            id: w.id,
            label: name,
            subLabel: code ? `[${code}]` : undefined,
            meta: [typeLabel, w.managerName ? `Quản lý: ${w.managerName}` : null].filter(Boolean).join(' · ') || undefined,
            searchTags: [code, name, w.type, w.managerName].filter(Boolean).join(' ').toLowerCase(),
            original: w,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Purchase Orders ────────────────────────────────────

export function usePurchaseOrderLookup(vendorId?: string, status?: string | string[], enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['purchase-orders-lookup', vendorId || 'all', status || 'all', search || ''],
        queryFn: () => trpcClient.purchaseOrders.list.query({ page: 1, limit, vendorId: vendorId || undefined, status: status as any, search }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((po: any) => {
        const poNum = po.poNumber || po.id;
        const vendorName = po.vendor?.shortName || po.vendor?.name || po.vendorName || '';
        return {
            id: po.id,
            label: poNum,
            subLabel: vendorName ? `[${vendorName}]` : undefined,
            meta: po.totalAmount ? `Tổng: ${Number(po.totalAmount).toLocaleString('vi-VN')} đ` : undefined,
            searchTags: [poNum, vendorName].filter(Boolean).join(' ').toLowerCase(),
            original: po,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

// ── Deliveries ─────────────────────────────────────────

export function useDeliveryLookup(customerId?: string, status?: string, enabled = true, search?: string, limit = 100) {
    const { data, isLoading } = useQuery({
        queryKey: ['deliveries-lookup', customerId || 'all', status || 'all', search || ''],
        queryFn: () => trpcClient.deliveries.list.query({ page: 1, limit, customerId: customerId || undefined, status: status as any, search }),
        staleTime: 10 * 1000,
        gcTime: 60 * 1000,
        enabled,
    });

    const items = (data as any)?.items || (Array.isArray(data) ? data : []);
    const options: FKOption[] = items.map((d: any) => {
        const dId = d.deliveryId || d.id;
        const custName = d.customer?.shortName || d.customer?.name || d.customerName || '';
        return {
            id: d.id,
            label: dId,
            subLabel: custName ? `[${custName}]` : undefined,
            searchTags: [dId, custName].filter(Boolean).join(' ').toLowerCase(),
            original: d,
        };
    }).sort((a: FKOption, b: FKOption) => a.label.localeCompare(b.label, 'vi'));

    return { options, isLoading };
}

/**
 * Ensures that the currently selected FK value exists in options.
 * Prevents form state from dropping existing DB relations when editing.
 */
export function ensureFKOption(
    options: FKOption[] = [],
    currentId?: string | null,
    currentLabel?: string | null,
    original?: any,
): FKOption[] {
    if (!currentId) return options;
    if (options.some((o) => o.id === currentId)) return options;
    return [
        {
            id: currentId,
            label: currentLabel || currentId,
            searchTags: (currentLabel || currentId).toLowerCase(),
            original,
        },
        ...options,
    ];
}


